import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  markTicketEmailRead,
  sendTicketEmail,
  ticketConversationQuery,
  ticketEmailComposeQuery,
  ticketSmsQuery,
  trashTicketEmail,
  type TicketEmail,
} from '../api/easeedesk'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import './TicketMessages.css'

/**
 * Cele trei file de mesaje ale fisei: "Conversation" (emailuri), "Chat" si
 * "SMS".
 *
 * Toate trei arata acelasi lucru - ce s-a vorbit pe tichetul asta, in ordinea
 * timpului - deci impartasesc cutia goala si chenarul de eroare, si se deosebesc
 * numai prin cum arata un mesaj.
 *
 * Istoricul se citeste; scrierile se muta una cate una. CHAT: mesajul
 * operatorului pleaca prin `POST tickets/{id}/chat` (`TicketChatWrites`), cu
 * casuta de sub fir din `chat_conversation.js` - Enter trimite, Ctrl+Enter
 * pune rand nou. CONVERSATION: "New email" din antet si "Reply" de pe un
 * email deschid formularul lui `conversation.js` (`composeForm`: To, Cc,
 * Subject, Message, fisiere), care scrie prin `POST tickets/{id}/conversation`
 * (`TicketEmailWrites`); deschiderea unui email primit il marcheaza citit
 * (`markAsRead`, un POST al lui), cosul e `moveToTrash`. SMS: numai
 * istoricul. Trimiterea ("New SMS") a fost scoasa pe 18 septembrie 2026 - pleca
 * pe o coada RabbitMQ pe care n-o mai citeste nimeni - iar fila e ascunsa din
 * lista fisei (`TicketTabs::SMS_TAB` in v4/api).
 */

function Empty({ what }: { what: string }) {
  return (
    <div className="note note-info tk-msg-empty">
      <i className="fas fa-comment" aria-hidden="true"></i> No {what}.
    </div>
  )
}

function Failed({ message }: { message: string }) {
  return (
    <div className="alert alert-danger" role="alert">
      Something went wrong: {message}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Conversation                                                             */
/* ------------------------------------------------------------------------ */

/* Butoanele de filtrare, asa cum le desena `conversation.js` din portal:
   Inbox, Sent, Trash, All, fiecare cu semnul lui. Serverul trimite lista in
   alta ordine (All primul); aici se aseaza ca in portal. */
const FOLDER_LOOK: Record<string, { order: number; icon?: string }> = {
  inbox: { order: 0, icon: 'fa fa-inbox' },
  sent: { order: 1, icon: 'fa fa-level-up-alt' },
  trash: { order: 2, icon: 'fas fa-trash' },
  all: { order: 3 },
}

/* `moment(created).format('H:mm, MMMM Do')` din portal: "11:00, September 2nd". */
function emailDate(raw: string): string {
  const at = new Date(raw.replace(' ', 'T'))
  if (Number.isNaN(at.getTime())) return raw
  const day = at.getDate()
  const rest = day % 100
  const suffix = rest >= 11 && rest <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][day % 10 > 3 ? 0 : day % 10]
  const month = at.toLocaleDateString('en-US', { month: 'long' })
  const minutes = String(at.getMinutes()).padStart(2, '0')
  return `${at.getHours()}:${minutes}, ${month} ${day}${suffix}`
}

/* `composeForm` din conversation.js: To, Cc (selectize cu contactele firmei -
   aici o lista de adrese despartite cu virgula, cu contactele ca sugestii),
   Subject, Message, fisierele, "Attach file" si "Send email". */
function EmailCompose({ id, replyTo, onDone }: { id: number; replyTo: number | null; onDone(): void }) {
  const { data, error } = useQuery(ticketEmailComposeQuery(id, replyTo))
  const client = useQueryClient()
  const [to, setTo] = useState<string | null>(null)
  const [cc, setCc] = useState<string | null>(null)
  const [subject, setSubject] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const picker = useRef<HTMLInputElement>(null)

  const split = (raw: string) => raw.split(/[;,\s]+/).map((one) => one.trim()).filter((one) => one !== '')

  const send = useMutation({
    mutationFn: () =>
      sendTicketEmail(id, {
        to: split(to ?? data?.emailTo.join(', ') ?? ''),
        cc: split(cc ?? data?.emailCc.join(', ') ?? ''),
        subject: subject ?? data?.emailSubject ?? '',
        message: message ?? data?.emailBody ?? '',
        replyTo,
        files,
      }),
    onSuccess: (reply) => {
      toast(reply.sent === reply.recipients ? 'Email sent.' : `Sent to ${reply.sent} of ${reply.recipients} addresses.`, 'Conversation', reply.sent === reply.recipients ? 'success' : 'warning')
      void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id, 'conversation'] })
      void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id, 'tabs'] })
      onDone()
    },
  })

  if (error) return <Failed message={error.message} />
  if (!data) return <Spinner />

  const toValue = to ?? data.emailTo.join(', ')
  const ccValue = cc ?? data.emailCc.join(', ')
  const subjectValue = subject ?? data.emailSubject
  const messageValue = message ?? data.emailBody

  const row = (label: string, input: ReactNode) => (
    <div className="form-group clearfix">
      <label className="control-label col-md-2">
        <span className="color">{label}</span>
      </label>
      <div className="col-sm-10">{input}</div>
    </div>
  )

  return (
    <form
      className="compose-email-form form-horizontal tk-msg-compose"
      method="post"
      encType="multipart/form-data"
      onSubmit={(ev) => {
        ev.preventDefault()
        if (split(toValue).length === 0 || send.isPending) return
        send.mutate()
      }}
    >
      {row('To', <input className="form-control" name="emailTo" required list={`contacts-${id}`} value={toValue} onChange={(ev) => setTo(ev.target.value)} placeholder="address@example.com, ..." />)}
      {row('Cc', <input className="form-control" name="emailCc" list={`contacts-${id}`} value={ccValue} onChange={(ev) => setCc(ev.target.value)} />)}
      <datalist id={`contacts-${id}`}>
        {data.contacts.map((one) => (
          <option key={one.email} value={one.email}>
            {one.name}
          </option>
        ))}
      </datalist>
      {row('Subject', <input className="form-control" name="emailSubject" type="text" value={subjectValue} onChange={(ev) => setSubject(ev.target.value)} />)}
      {row('Message', <textarea className="form-control compose-body-email" name="emailMessage" rows={10} value={messageValue} onChange={(ev) => setMessage(ev.target.value)}></textarea>)}
      {files.length > 0 && (
        <div className="form-group clearfix">
          <div className="col-sm-10 col-sm-offset-2 tk-msg-files">
            {files.map((file, index) => (
              <span className="email-attachment" key={`${file.name}-${index}`}>
                <i className="fa fa-paperclip" aria-hidden="true"></i> {file.name}{' '}
                <button type="button" className="btn btn-xs btn-link" aria-label={`Remove ${file.name}`} onClick={() => setFiles((have) => have.filter((_, i) => i !== index))}>
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
      {send.isError && <p className="text-danger">{(send.error as Error).message}</p>}
      <div className="form-group clearfix">
        <div className="col-sm-10 col-sm-offset-2">
          <input
            type="file"
            className="email-attachment hidden"
            name="files[]"
            multiple
            ref={picker}
            onChange={(ev) => {
              const chosen = ev.target.files ? Array.from(ev.target.files) : []
              if (chosen.length > 0) setFiles((have) => [...have, ...chosen])
              ev.target.value = ''
            }}
          />
          <button type="button" className="btn btn-default btn-sm add-email-attachment" onClick={() => picker.current?.click()}>
            <i className="fa fa-paperclip" aria-hidden="true"></i> Attach file
          </button>{' '}
          <button type="submit" className="btn btn-primary btn-sm send-email" disabled={split(toValue).length === 0 || send.isPending}>
            {send.isPending ? 'Sending...' : 'Send email'}
          </button>{' '}
          <button type="button" className="btn btn-default btn-sm" onClick={onDone}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  )
}

export function TicketConversation({ id }: { id: number }) {
  /* Portalul pornea pe "All" (`triggerFilter('all')`), desi butonul aprins in
     macheta era "Inbox" - deci lista si butonul se contraziceau la deschidere. */
  const [folder, setFolder] = useState('all')
  const { data, error } = useQuery(ticketConversationQuery(id, folder))
  const [open, setOpen] = useState(0)
  /* `compose`: null = lista; 0 = email nou; altceva = raspuns la emailul cu numarul ala. */
  const [compose, setCompose] = useState<number | null>(null)
  const client = useQueryClient()

  const trash = useMutation({
    mutationFn: (email: number) => trashTicketEmail(id, email),
    onSuccess: () => {
      setOpen(0)
      void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id, 'conversation'] })
    },
    onError: (err: Error) => toast(err.message, 'Conversation', 'error'),
  })

  if (error) return <Failed message={error.message} />
  if (!data) return <Spinner />

  return (
    <div className={`mailbox mailbox-${id}`}>
      <div className="conversation_header">
        <button
          type="button"
          className="btn btn-sm btn-success pull-right compose-email"
          onClick={() => {
            setOpen(0)
            setCompose(0)
          }}
        >
          <i className="fas fa-pencil-alt" aria-hidden="true"></i> New email
        </button>
        <div className="btn-group" role="group">
          {[...data.folders]
            .sort((a, b) => (FOLDER_LOOK[a.key]?.order ?? 9) - (FOLDER_LOOK[b.key]?.order ?? 9))
            .map((one) => (
              <button
                key={one.key}
                type="button"
                className={`btn btn-theme-inverse btn-transparent btn-sm${folder === one.key ? ' active' : ''}`}
                onClick={() => {
                  setFolder(one.key)
                  setOpen(0)
                  setCompose(null)
                }}
              >
                {FOLDER_LOOK[one.key]?.icon && (
                  <>
                    <i className={FOLDER_LOOK[one.key].icon} aria-hidden="true"></i>{' '}
                  </>
                )}
                {one.label}
              </button>
            ))}
        </div>
      </div>

      <div className="conversation_content">
        {compose !== null ? (
          <EmailCompose id={id} replyTo={compose === 0 ? null : compose} onDone={() => setCompose(null)} />
        ) : data.messages.length === 0 ? (
          <Empty what="messages" />
        ) : (
          <table className="conversation-list">
            <tbody>
              {data.messages.map((message) => (
                <EmailRow
                  key={message.id}
                  ticket={id}
                  message={message}
                  open={open === message.id}
                  onOpen={() => setOpen(open === message.id ? 0 : message.id)}
                  onReply={() => setCompose(message.id)}
                  onTrash={() => trash.mutate(message.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function EmailRow({
  ticket,
  message,
  open,
  onOpen,
  onReply,
  onTrash,
}: {
  ticket: number
  message: TicketEmail
  open: boolean
  onOpen(): void
  onReply(): void
  onTrash(): void
}) {
  const client = useQueryClient()

  /* `conversation.js` r.107: la deschiderea unui email PRIMIT necitit, `markAsRead`. */
  const marked = useRef(false)
  useEffect(() => {
    if (!open || !message.unread || message.kind !== 'received' || marked.current) return
    marked.current = true
    void markTicketEmailRead(ticket, message.id)
      .then(() => client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticket, 'conversation'] }))
      .catch(() => undefined)
  }, [client, message.id, message.kind, message.unread, open, ticket])

  const badge = message.trash
    ? { className: 'trash', icon: 'fa fa-trash' }
    : message.kind === 'sent'
      ? { className: '', icon: 'fa fa-level-up-alt' }
      : { className: 'received', icon: 'fa fa-inbox' }

  return (
    <>
      <tr className={`conversation-message${message.unread ? ' newMessage' : ''}${open ? ' selected' : ''}`} onClick={onOpen}>
        <td className="mailbox-item" width="35px">
          <span className={`badge message-type ${badge.className}`}>
            <i className={badge.icon} aria-hidden="true"></i>
          </span>
        </td>
        <td className="mailbox-item">
          <h6>{message.sender}</h6>
          <span className="message-to">to: {message.to.join('; ')}</span>
        </td>
        <td className="mailbox-item">
          {message.attachments.length > 0 && (
            <span className="badge bg-primary">
              {message.attachments.length} <i className="fa fa-paperclip" aria-hidden="true"></i>
            </span>
          )}{' '}
          {message.subject}
        </td>
        <td className="mailbox-item align-right date-from">
          <i className="far fa-clock" aria-hidden="true"></i> {emailDate(message.at)}
        </td>
      </tr>

      {open && (
        <tr className="message-preview">
          <td colSpan={4}>
            <section className="panel">
              <header className="panel-heading">
                <div className="mail-title">
                  <h5>{message.subject}</h5>
                </div>
                {/* `mailActions` din conversation.js: cosul (numai pe un email viu) si Reply. */}
                <div className="mail-actions tk-msg-actions">
                  {!message.trash && (
                    <button type="button" className="btn btn-xs btn-theme" title="Move to trash" aria-label="Move to trash" onClick={onTrash}>
                      <i className="fas fa-trash" aria-hidden="true"></i>
                    </button>
                  )}{' '}
                  <button type="button" className="btn btn-xs btn-inverse" onClick={onReply}>
                    <i className="fa fa-share" aria-hidden="true"></i> Reply
                  </button>
                </div>
                <div className="mail-contacts">
                  <p className="tk-msg-sender">{message.sender}</p>
                  <p>
                    <span className="badge-from">To</span> {message.to.join('; ')}
                  </p>
                  {message.cc.length > 0 && (
                    <p>
                      <span className="badge-from">Cc</span> {message.cc.join('; ')}
                    </p>
                  )}
                </div>
              </header>
              <div className="panel-body">
                {message.attachments.length > 0 && (
                  <div className="mail-attachments">
                    {message.attachments.map((file) => (
                      <span className="email-attachment" key={file.name}>
                        <i className="fa fa-paperclip" aria-hidden="true"></i> {file.name}
                      </span>
                    ))}
                  </div>
                )}
                {/*
                 * Corpul emailului e HTML scris de cine a trimis mesajul, deci
                 * de oricine de pe internet. `sandbox` gol inseamna: fara
                 * scripturi, fara formulare, si fara acces la pagina din jur -
                 * o foaie de hartie, nu un program. Portalul il punea intr-un
                 * iframe de ACEEASI origine, care poate ajunge inapoi la pagina.
                 */}
                <iframe className="tk-msg-body" title={message.subject} sandbox="" srcDoc={message.body}></iframe>
              </div>
            </section>
          </td>
        </tr>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ */
/*  Chat                                                                     */
/* ------------------------------------------------------------------------ */

/* Fila "Chat" sta in fisierul ei (TicketChat.tsx) de la redesenarea din
   13 octombrie 2026; numele ramane exportat si de aici, cum il cer TicketPage
   si probele. */
export { TicketChat } from './TicketChat'

/* ------------------------------------------------------------------------ */
/*  SMS                                                                      */
/* ------------------------------------------------------------------------ */

type SmsFilter = 'all' | 'sent' | 'received'

const SMS_FILTERS: { key: SmsFilter; label: string }[] = [
  { key: 'sent', label: 'Sent' },
  { key: 'received', label: 'Received' },
  { key: 'all', label: 'All' },
]

const SMS_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function ordinal(day: number): string {
  const rest = day % 100
  if (rest >= 11 && rest <= 13) return `${day}th`
  switch (day % 10) {
    case 1: return `${day}st`
    case 2: return `${day}nd`
    case 3: return `${day}rd`
    default: return `${day}th`
  }
}

/** Ora unui SMS cum o scria portalul: `moment(posted).format('H:mm, MMMM Do')`. */
function smsWhen(at: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(at)
  if (!m) return at
  return `${Number(m[4])}:${m[5]}, ${SMS_MONTHS[Number(m[2]) - 1] ?? m[2]} ${ordinal(Number(m[3]))}`
}

/*
 * Aceeasi asezare ca in portal (`public/js/easeedesk/sms_conversation.js`):
 * un rand de filtre Sent / Received / All deasupra, apoi firul `.messaging`
 * din `app.css` - primite in stanga, cu bula gri, trimise in dreapta, cu bula
 * verde. Filtrele cern lista deja adusa; portalul o cerea din nou serverului
 * la fiecare apasare, dar aratau la fel.
 */
export function TicketSms({ id }: { id: number }) {
  const { data, error } = useQuery(ticketSmsQuery(id))
  const [filter, setFilter] = useState<SmsFilter>('all')

  if (error) return <Failed message={error.message} />
  if (!data) return <Spinner />

  if (data.messages.length === 0) {
    return (
      <div className={`sms-container tk-sms sms-container-${id}`}>
        <Empty what="messages" />
      </div>
    )
  }

  const shown = filter === 'all' ? data.messages : data.messages.filter((m) => m.kind === filter)

  return (
    <div className={`sms-container tk-sms sms-container-${id}`}>
      <div className="sms_conversation_header">
        <div className="btn-group" role="group" aria-label="Filter messages">
          {SMS_FILTERS.map((one) => (
            <button
              key={one.key}
              type="button"
              data-filter={one.key}
              className={`btn btn-theme-inverse btn-transparent btn-sm${filter === one.key ? ' active' : ''}`}
              onClick={() => setFilter(one.key)}
            >
              {one.label}
            </button>
          ))}
        </div>
      </div>
      <div className="sms_conversation_content messaging">
        <ul className="sms-list messaging-history">
          {shown.length === 0 ? (
            <li className="tk-sms-none">
              <div className="note note-info">
                <i className="fas fa-comment" aria-hidden="true"></i> No messages.
              </div>
            </li>
          ) : (
            shown.map((message) =>
              message.kind === 'sent' ? (
                <li key={message.id} className="other-message">
                  <div className="message-data">
                    <span className="message-data-time">{smsWhen(message.at)}</span>
                    <span className="message-data-name">
                      {message.sender}{' '}
                      <span className="tk-sms-to">
                        <i className="fa fa-share" aria-hidden="true"></i> {message.phone}
                      </span>
                    </span>{' '}
                    <i className="fas fa-circle me" aria-hidden="true"></i>
                  </div>
                  <div className="message">{message.message}</div>
                </li>
              ) : (
                <li key={message.id} className="my-message">
                  <div className="message-data">
                    <i className="fas fa-circle online" aria-hidden="true"></i>{' '}
                    <i className="fa fa-reply" aria-hidden="true"></i>
                    <span className="message-data-name">{message.phone}</span>
                    <span className="message-data-time">{smsWhen(message.at)}</span>
                  </div>
                  <div className="message">{message.message}</div>
                </li>
              ),
            )
          )}
        </ul>
      </div>
    </div>
  )
}
